import { Badge, Button, IconCheckCircle } from '@indipia/ui-core';
import type { BillingCycle, PricingTier } from '../api/billingApi';
import type { TierCopy } from '../constants/tierCopy';
import { resolveCtaLabel } from '../lib/ctaResolution';

/** 요금제 카드 4장 그리드 — 3+1로 남는 줄이 생기지 않게 1·2·4열만 쓴다(요금제 비교·변경 화면 공용).
 *  ponytail: 뷰포트 기준이라 LNB 열림 여부는 반영하지 않음 — 좁아지면 .card-list.cols-1-2-3-4처럼 data-lnb 분기 */
export const PRICING_GRID_CLASS = 'grid grid-cols-1 gap-md lg:grid-cols-2 xl:grid-cols-4';

export type PricingCardState = 'guest' | 'trial' | 'current' | 'upgrade' | 'downgrade' | 'cycle-change';

export interface PricingCardProps {
  tier: PricingTier;
  copy: TierCopy;
  billingCycle: BillingCycle;
  freeMonthsAnnual: number;
  state: PricingCardState;
  /** 체험 종료 임박(3일 이내) 시 잔여일수 */
  trialDaysLeft?: number | null;
  /** 이 스튜디오가 결제한 적 한 번이라도 있는지 — STARTER·PRO의 "시작하기"/"변경하기" 문구 분기용 */
  hasEverPaid: boolean;
  /** 지금 체험 중인지(subStatus==='TRIAL') — 체험 중엔 hasEverPaid와 무관하게 "변경하기"로 고정한다
   *  (2026-09-17, 사용자 결정: 체험도 이미 요금제를 선택해 이용 중인 상태라 "시작"이 아니라 "변경"). */
  isTrialing: boolean;
  /** 체험 버튼 문구에 쓸 개월 수 — 추천 코드가 있으면 보너스가 더해진 값(tierCopy.ts getTrialMonths) */
  trialMonths: number;
  /** 요금제 변경 화면에서 고른 카드 — 테두리로 선택 상태를 표시한다 */
  selected?: boolean;
  onCtaClick: (tierCode: PricingTier['tierCode']) => void;
}

function formatWon(n: number) {
  return n.toLocaleString('ko-KR');
}

export function PricingCard({
  tier,
  copy,
  billingCycle,
  freeMonthsAnnual,
  state,
  trialDaysLeft,
  hasEverPaid,
  isTrialing,
  trialMonths,
  selected = false,
  onCtaClick,
}: PricingCardProps) {
  const isAnnual = billingCycle === 'annual';
  const displayPrice = isAnnual ? tier.annualMonthlyDisplay : tier.monthlyPrice;
  const hasPrice = tier.monthlyPrice > 0;
  const isCurrent = state === 'current';
  const featured = tier.featuredYn ?? tier.tierCode === 'PRO';

  const ctaLabel = resolveCtaLabel(state, copy.ctaLabel, hasEverPaid, billingCycle, trialMonths, isTrialing);
  // 'current'(지금 이용 중인 티어)일 때도 버튼을 항상 보여준다 — disabled 상태로 "이용중" 라벨을
  // 그대로 노출해서, 지금 쓰고 있는 요금제가 어느 카드인지 한눈에 알 수 있게 한다(사용자 결정,
  // 2026-09-21 — 이전엔 버튼 자체를 숨겼는데, 그러면 카드 사이에 "왜 이것만 버튼이 없지" 외엔
  // 아무 표식도 남지 않아 정작 "이용중"이라는 걸 알려줄 방법이 없었다).
  const captionText =
    tier.tierCode === 'FREE'
      ? `현장 ${tier.maxConcurrentSiteLabel} · 카드 등록 없이 바로`
      : state === 'trial'
        ? '체험 종료 후 자동 전환 안내'
        : isCurrent
          ? ' '
          : '결제 즉시 이용 가능해요';
  const annualNote =
    isAnnual && hasPrice ? `연 ${formatWon(tier.annualTotal)}원 결제 · ${freeMonthsAnnual}개월 무료` : ' ';

  return (
    <article
      aria-current={selected || undefined}
      className={`app-card flex h-full flex-col transition-all hover:-translate-y-1 hover:shadow-emphasis ${
        selected ? 'border-group ring-2 ring-group' : featured ? 'border-group ring-1 ring-group' : ''
      }`}
    >
      <div className="body flex h-full flex-col">
        {/* 추천 배지 줄은 모든 카드에 같은 높이로 둔다 — 추천 카드만 제목·가격이 아래로 밀려 카드끼리 줄이 어긋나지 않게 */}
        <div className={`mb-04 h-06 items-center justify-center ${featured || selected ? 'flex' : 'hidden lg:flex'}`}>
          {selected ? (
            <Badge status="info">선택한 요금제</Badge>
          ) : (
            featured && <Badge status="warning">가장 많이 선택해요</Badge>
          )}
        </div>
        {/* trialEndDtm은 체험이 끝나고 FREE로 강등된 뒤에도 지워지지 않고 남는다(§21 참고) — 지난
            체험의 날짜가 그대로 남아 있으면 음수가 나오므로 0 이상일 때만 배지를 보여준다. */}
        {isCurrent && trialDaysLeft != null && trialDaysLeft >= 0 && trialDaysLeft <= 3 && (
          <Badge status="warning" className="mb-03 self-start">
            체험 종료까지 {trialDaysLeft}일 남음
          </Badge>
        )}

        <h3 className="text-title-md text-text">{copy.displayName}</h3>
        <p className="mt-03 text-body-sm text-text-muted">{copy.description}</p>

        <div className="mt-06">
          <span className="text-display-sm font-bold text-text">₩{formatWon(displayPrice)}</span>
          <span className="ml-02 text-body-sm text-text-muted">/월</span>
        </div>
        <p className="mt-02 text-caption text-text-muted">{annualNote}</p>

        <ul className="mt-07 flex-1 space-y-05">
          {copy.featureList.map((feature) => (
            <li key={feature} className="flex items-start gap-sm">
              <IconCheckCircle width={16} height={16} className="mt-01 shrink-0 text-group" />
              <span className="text-body-sm text-text">{feature}</span>
            </li>
          ))}
        </ul>

        <Button
          variant={isCurrent ? 'secondary' : copy.ctaVariant}
          disabled={isCurrent}
          className="mt-08 w-full"
          onClick={() => onCtaClick(tier.tierCode)}
        >
          {ctaLabel}
        </Button>
        <p className="mt-05 text-center text-caption text-text-muted">{captionText}</p>
        <div className="h-06" aria-hidden="true" />
      </div>
    </article>
  );
}
